import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { CHAT_FILE_PANE_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the chat file pane gate (`/api/flags/chat-file-pane`, Conductor
 * experiment `symphonyChatFilePaneWeb`): whether a chat-opened file docks beside the conversation and
 * refreshes live, or opens in the modal as before.
 *
 * Fails CLOSED to the modal. A dev-tools Feature toggles override wins over the server. Memoized per page
 * load, so a pane never turns into a modal under someone mid-read; only a real answer is remembered, so one
 * failed read is asked again rather than hiding the pane until a reload.
 */
const GATE_TIMEOUT_MS = 2500;

let cached: Promise<boolean> | null = null;
let known: boolean | null = null;

export function chatFilePaneEnabled(): Promise<boolean> {
  const forced = toggleOverride(CHAT_FILE_PANE_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/chat-file-pane`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) return (known = ((await res.json()) as boolean) === true);
    } catch {
      // closed for now; asked again by the next reader
    }
    cached = null;
    return false;
  })();
  return cached;
}

export function useChatFilePaneEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => {
    const forced = toggleOverride(CHAT_FILE_PANE_SPEC);
    return forced !== undefined ? forced === 'true' : (known ?? false);
  });
  useEffect(() => {
    let live = true;
    void chatFilePaneEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
